/**
 * ListInvitationBanner Component
 *
 * Shown on the list detail screen when the current user has a pending
 * invitation. Provides Accept and Decline buttons.
 */

import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import type { List } from '../types';
import { useRespondToListInvitation } from '../hooks/use-list-member-mutations';
import { router } from 'expo-router';
import { useAppTheme } from '@/providers/theme-provider';

interface ListInvitationBannerProps {
  list: List;
}

export function ListInvitationBanner({ list }: ListInvitationBannerProps) {
  const theme = useAppTheme();
  const respondMutation = useRespondToListInvitation();
  const isProcessing = respondMutation.isPending;

  const handleAccept = () => {
    respondMutation.mutate(
      { listId: list.id, action: 'accept' },
    );
  };

  const handleDecline = () => {
    respondMutation.mutate(
      { listId: list.id, action: 'decline' },
      { onSuccess: () => router.back() },
    );
  };

  return (
    <View className="flex-1 items-center justify-center px-6">
      <View className="w-full max-w-sm rounded-2xl bg-surface-secondary p-6" style={{ gap: 16 }}>
        <View className="items-center" style={{ gap: 8 }}>
          <View className="w-14 h-14 rounded-full bg-primary/10 items-center justify-center">
            <Ionicons name="mail-outline" size={28} color={theme.colors.primary} />
          </View>

          <Text className="text-lg font-bold text-foreground text-center">
            You&apos;ve been invited
          </Text>

          <Text className="text-sm text-muted-foreground text-center leading-5">
            You&apos;ve been invited to join &quot;{list.name}&quot;
            {list.description ? ` -- ${list.description}` : ''}
          </Text>
        </View>

        {/* Stats */}
        <View className="flex-row justify-center" style={{ gap: 16 }}>
          <View className="flex-row items-center">
            <Ionicons name="layers-outline" size={14} className="text-muted-foreground" />
            <Text className="ml-1 text-sm text-muted-foreground">
              {list.itemCount} {list.itemCount === 1 ? 'item' : 'items'}
            </Text>
          </View>
          <View className="flex-row items-center">
            <Ionicons name="people-outline" size={14} className="text-muted-foreground" />
            <Text className="ml-1 text-sm text-muted-foreground">
              {list.memberCount} {list.memberCount === 1 ? 'member' : 'members'}
            </Text>
          </View>
        </View>

        {/* Action buttons */}
        {isProcessing ? (
          <View className="py-3 items-center">
            <ActivityIndicator size="small" color={theme.colors.primary} />
          </View>
        ) : (
          <View className="flex-row" style={{ gap: 12 }}>
            <View className="flex-1">
              <Button
                variant="outline"
                onPress={handleDecline}
                disabled={isProcessing}
              >
                <Text className="text-sm font-semibold text-foreground">Decline</Text>
              </Button>
            </View>
            <View className="flex-1">
              <Button
                variant="default"
                onPress={handleAccept}
                disabled={isProcessing}
              >
                <Text className="text-sm font-semibold text-primary-foreground">Accept</Text>
              </Button>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}
